import React, {useEffect, useMemo} from 'react';
import {classes, style} from './subscribe-action-form.st.css';
import {
    AsyncValueStates,
    ButtonType,
    Checkbox,
    Icon,
    ListView,
    listViewCreateDataSource,
    ListViewItemRoot,
    ListViewSelectionType,
    NavigationOrientation,
    TextBoxTypes,
    useAsyncOperation,
    useFormController,
    useRenderState
} from '@wix/devzai-utils-react';
import IconChecked from './icons/checked.svg';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {
    arrayOrderBy,
    compareOrderedValues,
    evaluateFunction,
    HtmlObjectResizeMode,
    IdGenerator,
    validateIsEmail,
    validateIsMobilePhone
} from '@wix/devzai-utils-common';
import {
    DoppeActionTypeWidgetContentSeparator
} from '../doppe-action-type-widget-content-separator/doppe-action-type-widget-content-separator';
import {IDoppeViewerActionContext} from '../../doppe-sdk';
import {
    DoppeActionTypeSubscribe,
    SubscribeActionFormSubscriptionOptions
} from '../../doppe-action-types/doppe-action-type-subscribe/doppe-action-type-subscribe-metadata';
import {DoppeActionWidgetTextBox} from '../doppe-action-widget-text-box/doppe-action-widget-text-box';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {DoppeActionTypeWidgetLayout} from '../doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {DoppePageMediaView} from '../doppe-page-media-view/doppe-page-media-view';
import {
    DoppeHideableValue,
    doppeHideableValueCreateHidden,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../client-server-common';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerCtaButton
} from '../doppe-action-type-widget-inner-cta-button/doppe-action-type-widget-inner-cta-button';

type MenuItem = {
    id: SubscribeActionFormSubscriptionOptions;
    title: string;
};

export const SubscribeActionForm = React.memo(function SubscribeActionForm(props: SubscribeActionForm.Props) {
    const {
        actionContext,
        title,
        description = doppeHideableValueCreateHidden(''),
        consentText,
        subscribeButtonText = 'Subscribe',
        showConsentCheckbox,
        defaultSubscribeOption,
        enableEmailSubscribeOption,
        enablePhoneSubscribeOption,
        submissionFunction,
        inputIdsNamespace,
        images,
        autoFocus = false,

        style: styleProp,
        className,
        ...htmlAttributes
    } = props;


    const [formSubmissionOperation, setFormSubmissionOperation] = useAsyncOperation();

    const renderState = useRenderState({
        getDefaultSubscribeOption: () => {
            if (
                (defaultSubscribeOption === SubscribeActionFormSubscriptionOptions.Email && enableEmailSubscribeOption) ||
                (defaultSubscribeOption === SubscribeActionFormSubscriptionOptions.Phone && enablePhoneSubscribeOption)
            ) {
                return defaultSubscribeOption
            } else if (enableEmailSubscribeOption) {
                return SubscribeActionFormSubscriptionOptions.Email;
            } else if (enablePhoneSubscribeOption) {
                return SubscribeActionFormSubscriptionOptions.Phone;
            } else {
                return SubscribeActionFormSubscriptionOptions.Email;
            }
        }
    })


    const resolvedInputIdsNamespace = inputIdsNamespace ?? IdGenerator.uniqueId();
    const checkboxId = `${resolvedInputIdsNamespace}_checkbox`;

    const doppeViewerLocales = useDoppeViewerLocales();

    const formController = useFormController<SubscribeActionForm.FormData>({
        subscribeOption: renderState.current.getDefaultSubscribeOption(),
        email: '',
        phoneNumber: '',
        hasConsent: false
    }, {
        validationFunction: (formData, errorsReporter) => {
            const subscribeOption = formData.subscribeOption;

            if (subscribeOption === SubscribeActionFormSubscriptionOptions.Email) {

                const email = formData.email.trim();

                if (email === '') {
                    errorsReporter.report('email', doppeViewerLocales['requiredField'])
                } else if (!validateIsEmail(email)) {
                    errorsReporter.report('email', doppeViewerLocales['enterValidEmail'])
                }

            } else {
                const phoneNumber = formData.phoneNumber.trim();

                if (phoneNumber === '') {
                    errorsReporter.report('phoneNumber', doppeViewerLocales['requiredField'])
                } else if (!validateIsMobilePhone(phoneNumber)) {
                    errorsReporter.report('phoneNumber', doppeViewerLocales['enterValidPhone'])
                }
            }

            if (showConsentCheckbox && !formData.hasConsent) {
                errorsReporter.report('hasConsent', doppeViewerLocales['requiredConsent'])
            }
        }
    })

    const menuItemsDataSource = useMemo(() => {

        const menuItems: MenuItem[] = [];

        if (enableEmailSubscribeOption) {
            menuItems.push({
                id: SubscribeActionFormSubscriptionOptions.Email,
                title: doppeViewerLocales['collectSubscribersEmailMenuItem']
            })
        }

        if (enablePhoneSubscribeOption) {
            menuItems.push({
                id: SubscribeActionFormSubscriptionOptions.Phone,
                title: doppeViewerLocales['collectSubscribersPhoneFieldMenuItem']
            })
        }

        const orderedMenuItems = arrayOrderBy(
            menuItems,
            menuItem => menuItem.id,
            (value1, value2) => compareOrderedValues(value1, value2, [defaultSubscribeOption], Infinity))

        return listViewCreateDataSource<MenuItem>(orderedMenuItems, {
            idFunction: item => item.id
        })
    }, [enableEmailSubscribeOption, enablePhoneSubscribeOption, defaultSubscribeOption]);

    useEffect(() => {
        formController.setValue('subscribeOption', renderState.current.getDefaultSubscribeOption());
    }, [enableEmailSubscribeOption, enablePhoneSubscribeOption, defaultSubscribeOption, renderState, formController])

    const emailInputError = formController.formFieldsErrors.email;
    const phoneNumberInputError = formController.formFieldsErrors.phoneNumber;

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {
                hasTextBeforeFields: title !== '' || doppeHideableValueIsVisibleAndNotEqualValue(description, ''),
            }, className)}
            actionContext={actionContext}
            isForm={true}
            noValidate={true}
            onSubmit={formController.createSubmitEventHandler(async (formData) => {
                try {
                    await setFormSubmissionOperation(submissionFunction(formData));

                    formController.resetFormState();
                } catch (error) {
                    console.error(error);
                }
            })}
        >
            <div
                className={style(classes.formRoot, FlexLayout.column.default)}

            >
                <DoppeActionTypeWidgetContentLayoutNewWrapper
                    fullWidthMainMedia={true}
                    mainMedia={images && images.length > 0 ? (
                        <DoppePageMediaView
                            className={style(classes.productMedia, {})}
                            mediaResource={images[0]}
                            layoutSpec={{autoHeight: true, maxHeight: 360}}
                            widthSpec={{contentRelativeWidth: 1}}
                            mediaResizeMode={HtmlObjectResizeMode.Cover}
                        />
                    ) : null}
                    footer={
                        <DoppeActionTypeWidgetInnerCtaButton
                            actionContext={actionContext}
                            className={style(classes.subscribeButton)}
                            buttonType={ButtonType.SubmitButton}
                            isLoading={formSubmissionOperation?.state === AsyncValueStates.Pending}
                        >
                            {subscribeButtonText}
                        </DoppeActionTypeWidgetInnerCtaButton>
                    }
                >
                    <DoppeActionTypeWidgetInnerTitle
                        className={style(classes.title, {})}
                    >
                        {title}
                    </DoppeActionTypeWidgetInnerTitle>
                    <DoppeActionTypeWidgetInnerDescription
                        className={style(classes.description, {})}
                    >
                        {description}
                    </DoppeActionTypeWidgetInnerDescription>
                    <div
                        className={style(classes.subscribeForm, {}, FlexLayout.column.default)}
                    >
                        {evaluateFunction(() => {

                            if (menuItemsDataSource.length > 1) {

                                return (
                                    <div
                                        className={style(classes.menuContainer, {})}
                                    >
                                        <ListView
                                            orientation={NavigationOrientation.Horizontal}
                                            selectionType={ListViewSelectionType.Single}
                                            className={style(classes.menu, FlexLayout.row.stretch, FlexLayout.justifyContent.toCenter)}
                                            onItemClick={eventData => {
                                                formController.setValue('subscribeOption', eventData.dataItem.id)
                                            }}
                                            renderItem={renderProps => {
                                                const {
                                                    dataItem
                                                } = renderProps;

                                                const currentSubscribeOption = formController.getValue('subscribeOption');

                                                return (
                                                    <ListViewItemRoot
                                                        className={style(classes.tabItem, {isSelected: currentSubscribeOption === dataItem.id}, CommonClasses.button)}
                                                    >
                                                        {dataItem.title}
                                                    </ListViewItemRoot>
                                                )
                                            }}
                                        >
                                            {menuItemsDataSource}
                                        </ListView>
                                        <DoppeActionTypeWidgetContentSeparator/>
                                    </div>
                                )
                            } else {
                                return null;
                            }
                        })}
                        <div
                            className={style(classes.subscribeFormContent, FlexLayout.column.default)}
                        >
                            <div className={style(classes.subscribeInput, FlexLayout.column.default)}>
                                {evaluateFunction(() => {
                                    const currentSubscribeOption = formController.getValue('subscribeOption');

                                    switch (currentSubscribeOption) {
                                        case SubscribeActionFormSubscriptionOptions.Email:
                                            return (
                                                <>
                                                    <DoppeActionWidgetTextBox
                                                        hasError={emailInputError !== undefined}
                                                        type={TextBoxTypes.Email}
                                                        shouldValidateValue={false}
                                                        placeholder={doppeViewerLocales['collectSubscribersEmailFieldPlaceholder']}
                                                        value={formController.getValue('email')}
                                                        onTextChange={value => {
                                                            formController.setValue('email', value);
                                                        }}
                                                        autoFocus={autoFocus}
                                                    />
                                                    {emailInputError !== undefined ? (
                                                        <div
                                                            className={style(classes.inputErrorMessage, {})}>{emailInputError}</div>
                                                    ) : null}
                                                </>
                                            )
                                        case SubscribeActionFormSubscriptionOptions.Phone:
                                            return (
                                                <>
                                                    <DoppeActionWidgetTextBox
                                                        type={TextBoxTypes.PhoneNumber}
                                                        hasError={phoneNumberInputError !== undefined}
                                                        placeholder={doppeViewerLocales['collectSubscribersPhoneFieldPlaceholder']}
                                                        shouldValidateValue={false}
                                                        value={formController.getValue('phoneNumber')}
                                                        onTextChange={value => {
                                                            formController.setValue('phoneNumber', value);
                                                        }}
                                                        autoFocus={autoFocus}
                                                    />
                                                    {phoneNumberInputError !== undefined ? (
                                                        <div
                                                            className={style(classes.inputErrorMessage, {})}>{phoneNumberInputError}</div>
                                                    ) : null}
                                                </>
                                            )
                                        default:
                                            return null
                                    }
                                })}
                            </div>
                            <div>
                                <div
                                    className={style(classes.consentBox, {hasError: formController.formFieldsErrors.hasConsent !== undefined}, FlexLayout.row.alignToTop)}>
                                    {evaluateFunction(() => {
                                        return showConsentCheckbox ?
                                            (
                                                <Checkbox
                                                    className={style(classes.consentCheckbox)}
                                                    checked={formController.getValue('hasConsent')}
                                                    onCheckedChanged={value => {
                                                        formController.setValue('hasConsent', value);
                                                    }}
                                                    inputId={checkboxId}
                                                >
                                                    {(renderProps) => {
                                                        return (
                                                            <div
                                                                className={style(classes.consentCheckboxView, {}, FlexLayout.centerContent)}
                                                            >
                                                                {renderProps.checked ? (
                                                                    <Icon
                                                                        icon={IconChecked}
                                                                    />
                                                                ) : null}
                                                            </div>
                                                        )
                                                    }}
                                                </Checkbox>
                                            ) :
                                            null
                                    })}
                                    <label className={style(classes.consentText, {})}
                                           htmlFor={checkboxId}>{consentText}</label>
                                </div>
                                {
                                    formController.formFieldsErrors.hasConsent !== undefined ?
                                        <div
                                            className={style(classes.consentErrorMessageContainer, {})}
                                        >
                                            <div
                                                className={style(classes.inputErrorMessage, {})}
                                            >
                                                {doppeViewerLocales['youMustAgree']}
                                            </div>
                                        </div>

                                        :
                                        null
                                }
                            </div>
                        </div>
                    </div>
                </DoppeActionTypeWidgetContentLayoutNewWrapper>
            </div>
        </DoppeActionTypeWidgetLayout>
    );
});

export namespace SubscribeActionForm {

    export interface Props extends React.HTMLAttributes<any> {
        actionContext: IDoppeViewerActionContext;
        title: string;
        description?: DoppeHideableValue<string>;
        subscribeButtonText?: string;
        consentText: string | React.ReactElement;
        showConsentCheckbox: boolean;
        enablePhoneSubscribeOption: boolean;
        enableEmailSubscribeOption: boolean;
        defaultSubscribeOption: SubscribeActionFormSubscriptionOptions;
        submissionFunction: (formData: SubscribeActionForm.FormData) => Promise<void>;
        inputIdsNamespace?: string;
        images: DoppeActionTypeSubscribe.ActionSettings['images'];
        autoFocus?: boolean;
    }

    export type FormData = {
        subscribeOption: SubscribeActionFormSubscriptionOptions;
        email: string;
        phoneNumber: string;
        hasConsent: boolean;
    };
}